import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const QUICK_REMOVED = true;

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in successfully");
      navigate("/");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-white lg:grid-cols-2" data-testid="login-page">
      <div className="relative hidden lg:block">
        <img
          src="https://images.pexels.com/photos/11297769/pexels-photo-11297769.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Phone and laptop store"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-zinc-950/70" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Horizon logo" className="h-10 w-10 rounded-sm object-contain p-1" data-testid="login-logo" />
            <span className="font-heading text-xl font-bold tracking-tight">Horizon</span>
          </div>
          <div>
            <h1 className="font-heading text-4xl font-bold leading-tight tracking-tight lg:text-5xl">
              Manage your phone & laptop store in one place.
            </h1>
            <p className="mt-4 max-w-md text-base text-zinc-300">
              Stock, warehouse transfers, and sales invoices — all in one system.
            </p>
          </div>
          <p className="text-xs text-zinc-400">Horizon — Phone & Laptop Store</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 lg:p-12">
        <div className="w-full max-w-sm">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-900">Sign In</h2>
          <p className="mt-1 text-sm text-zinc-500">Use your email and password.</p>
          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="rounded-sm border-zinc-300 focus-visible:ring-blue-600"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="rounded-sm border-zinc-300 focus-visible:ring-blue-600"
              />
            </div>
            <Button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full rounded-sm bg-blue-600 font-medium text-white transition-transform hover:-translate-y-[1px] hover:bg-blue-700"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
